import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiClient, API_BASE_URL } from '../services/http.js';
import { useToast } from '../hooks/useToast.jsx';
import { useConfirm } from '../hooks/useConfirm.jsx';
import { ErrorRetryCard } from '../components/common/ErrorRetryCard.jsx';

const NOTE_TYPE_LABELS = {
  reflexao: 'Reflexão',
  favorito: 'Favorito',
  homilia: 'Homilia',
};

const NOTE_TYPE_ICONS = {
  reflexao: 'edit_note',
  favorito: 'bookmark',
  homilia: 'church',
};

const PAGE_SIZE = 10;

export default function CadernoPage() {
  const [notes, setNotes] = useState(null);
  const [notesError, setNotesError] = useState(null);
  const [total, setTotal] = useState(0);
  const [filter, setFilter] = useState('');
  const [tagFilter, setTagFilter] = useState('');
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [tagsInput, setTagsInput] = useState('');
  const [noteType, setNoteType] = useState('reflexao');
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState({ title: '', content: '', tags: '' });
  const [savingEdit, setSavingEdit] = useState(false);
  const [exporting, setExporting] = useState(false);
  const { showApiErrorToast } = useToast();
  const confirm = useConfirm();

  const buildQuery = (type, tag, offset) => {
    const params = new URLSearchParams();
    if (type) params.set('note_type', type);
    if (tag) params.set('tag', tag);
    params.set('limit', String(PAGE_SIZE));
    params.set('offset', String(offset));
    return params.toString();
  };

  const fetchNotes = async (type, tag, offset = 0, { append = false } = {}) => {
    setNotesError(null);
    try {
      const { res, data } = await apiClient.request(`/notes?${buildQuery(type, tag, offset)}`);
      if (res.ok) {
        const totalCount = Number(res.headers.get('X-Total-Count') || data.length);
        setTotal(totalCount);
        setNotes((prev) => (append ? [...(prev || []), ...data] : data));
        return;
      }
      setNotesError(apiClient.parseApiError(data, 'Não foi possível carregar seu caderno.'));
    } catch {
      setNotesError('Falha de conexão ao carregar seu caderno.');
    }
  };

  useEffect(() => { setTimeout(() => fetchNotes(filter, tagFilter, 0), 0); }, [filter, tagFilter]);

  const handleLoadMore = () => fetchNotes(filter, tagFilter, notes?.length || 0, { append: true });

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!content.trim() || saving) return;
    setSaving(true);
    try {
      const tags = tagsInput.split(',').map((t) => t.trim()).filter(Boolean);
      const { res, data } = await apiClient.request('/notes', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          content: content.trim(),
          title: title.trim() || null,
          note_type: noteType,
          tags: tags.length ? tags : null,
        }),
      });
      if (res.ok) {
        setTitle('');
        setContent('');
        setTagsInput('');
        fetchNotes(filter, tagFilter, 0);
      } else {
        showApiErrorToast(res, data, 'Não foi possível salvar a anotação.');
      }
    } catch {
      showApiErrorToast(null, null, 'Não foi possível salvar a anotação.', { onRetry: () => handleCreate(e) });
    }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!(await confirm({
      title: 'Apagar Anotação',
      message: 'Apagar esta anotação do seu caderno espiritual?',
      variant: 'danger',
      confirmText: 'Apagar'
    }))) return;
    try {
      const { res, data } = await apiClient.request(`/notes/${id}`, { method: 'DELETE' });
      if (res.ok) {
        setNotes((prev) => prev?.filter((n) => n.id !== id) ?? prev);
        setTotal((prev) => Math.max(0, prev - 1));
      } else {
        showApiErrorToast(res, data, 'Não foi possível apagar a anotação.', { onRetry: () => handleDelete(id) });
      }
    } catch {
      showApiErrorToast(null, null, 'Não foi possível apagar a anotação.', { onRetry: () => handleDelete(id) });
    }
  };

  const startEdit = (note) => {
    setEditingId(note.id);
    setEditDraft({ title: note.title || '', content: note.content, tags: (note.tags || []).join(', ') });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditDraft({ title: '', content: '', tags: '' });
  };

  const handleSaveEdit = async (id) => {
    if (!editDraft.content.trim() || savingEdit) return;
    setSavingEdit(true);
    try {
      const tags = editDraft.tags.split(',').map((t) => t.trim()).filter(Boolean);
      const { res, data } = await apiClient.request(`/notes/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          content: editDraft.content.trim(),
          title: editDraft.title.trim() || null,
          tags,
        }),
      });
      if (res.ok) {
        setNotes((prev) => prev?.map((n) => (n.id === id ? data : n)) ?? prev);
        cancelEdit();
      } else {
        showApiErrorToast(res, data, 'Não foi possível salvar a edição.');
      }
    } catch {
      showApiErrorToast(null, null, 'Não foi possível salvar a edição.', { onRetry: () => handleSaveEdit(id) });
    }
    setSavingEdit(false);
  };

  const handleExport = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const res = await fetch(`${API_BASE_URL}/notes/export`, { credentials: 'include' });
      if (!res.ok) {
        const data = await res.json().catch(() => null);
        showApiErrorToast(res, data, 'Não foi possível exportar o caderno.', { onRetry: handleExport });
        setExporting(false);
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'caderno-espiritual.md';
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      showApiErrorToast(null, null, 'Não foi possível exportar o caderno.', { onRetry: handleExport });
    }
    setExporting(false);
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim() || searching) return;
    setSearching(true);
    try {
      const { res, data } = await apiClient.request(`/notes/search?query=${encodeURIComponent(searchQuery.trim())}`);
      if (res.ok) {
        setSearchResults(data);
      } else {
        showApiErrorToast(res, data, 'Não foi possível buscar nas suas anotações.');
      }
    } catch {
      showApiErrorToast(null, null, 'Não foi possível buscar nas suas anotações.', { onRetry: () => handleSearch(e) });
    }
    setSearching(false);
  };

  return (
    <div className="min-h-screen bg-surface text-on-surface p-6">
      <div className="max-w-3xl mx-auto space-y-8">
        <div className="flex items-center justify-between gap-3">
          <Link to="/chat" className="hidden md:inline-flex items-center gap-2 px-4 py-2 bg-surface-container-low hover:bg-surface-container-high rounded-lg font-medium transition-colors border border-outline-variant">
            <span>←</span> Voltar para o Chat
          </Link>
          <h1 className="text-2xl font-bold">Caderno Espiritual</h1>
          <button
            onClick={handleExport}
            disabled={exporting}
            title="Exportar caderno em PDF"
            className="flex items-center gap-2 px-2 py-2 md:px-4 bg-surface-container-low hover:bg-surface-container-high rounded-lg font-medium transition-colors border border-outline-variant disabled:opacity-50 whitespace-nowrap"
          >
            <span className="material-symbols-outlined text-[18px]">picture_as_pdf</span>
            <span className="hidden md:inline">{exporting ? 'Exportando...' : 'Exportar Caderno'}</span>
          </button>
        </div>

        <p className="text-on-surface-variant text-sm">
          Salve reflexões, anotações de homilias e respostas favoritadas. Pergunte ao assistente sobre o
          que você já registrou (ex: "o que eu andei refletindo sobre perdão?") e ele buscará aqui também.
        </p>

        {/* Nova anotação */}
        <form onSubmit={handleCreate} className="bg-surface-container-low border border-outline-variant rounded-lg p-4 space-y-3">
          <h2 className="font-bold">Nova Anotação</h2>
          <div className="flex flex-col sm:flex-row gap-3">
            <select
              value={noteType}
              onChange={(e) => setNoteType(e.target.value)}
              className="w-full sm:w-auto p-2 bg-surface border border-outline-variant rounded text-on-surface"
            >
              <option value="reflexao">Reflexão</option>
              <option value="homilia">Homilia</option>
            </select>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Título (opcional)"
              className="flex-1 min-w-0 w-full p-2 bg-surface border border-outline-variant rounded"
            />
          </div>
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="Escreva sua reflexão, exame de consciência ou anotação de homilia..."
            rows={4}
            maxLength={8000}
            className="w-full p-2 bg-surface border border-outline-variant rounded resize-none"
          />
          <input
            type="text"
            value={tagsInput}
            onChange={(e) => setTagsInput(e.target.value)}
            placeholder="Tags (separadas por vírgula, opcional): gratidão, família..."
            className="w-full p-2 bg-surface border border-outline-variant rounded text-sm"
          />
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={saving || !content.trim()}
              className="px-6 py-2 bg-primary text-on-primary font-bold rounded hover:opacity-90 disabled:opacity-50"
            >
              {saving ? 'Salvando...' : 'Salvar'}
            </button>
          </div>
        </form>

        {/* Busca por similaridade */}
        <form onSubmit={handleSearch} className="flex gap-2">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Buscar nas suas anotações..."
            className="flex-1 p-2 bg-surface border border-outline-variant rounded"
          />
          <button type="submit" disabled={searching} className="px-4 py-2 bg-surface-container-high rounded font-bold hover:opacity-90 disabled:opacity-50">
            {searching ? 'Buscando...' : 'Buscar'}
          </button>
        </form>

        {searchResults !== null && (
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <h3 className="font-bold text-sm text-on-surface-variant">Resultados da busca</h3>
              <button onClick={() => setSearchResults(null)} className="text-xs text-primary hover:underline">Limpar</button>
            </div>
            {searchResults.length === 0 ? (
              <p className="text-sm text-on-surface-variant">Nenhuma anotação relacionada encontrada.</p>
            ) : (
              searchResults.map((r) => (
                <div key={r.id} className="bg-surface-container-low border border-outline-variant rounded-lg p-3">
                  <p className="text-xs text-on-surface-variant mb-1">{r.title || NOTE_TYPE_LABELS[r.note_type]}</p>
                  <p className="text-sm whitespace-pre-wrap">{r.content}</p>
                </div>
              ))
            )}
          </div>
        )}

        {/* Filtro por tipo e tag */}
        <div className="flex flex-wrap items-center gap-2">
          {['', 'reflexao', 'favorito', 'homilia'].map((t) => (
            <button
              key={t || 'todos'}
              onClick={() => setFilter(t)}
              className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${
                filter === t ? 'bg-primary text-on-primary border-primary' : 'border-outline-variant text-on-surface-variant hover:text-on-surface'
              }`}
            >
              {t ? NOTE_TYPE_LABELS[t] : 'Todos'}
            </button>
          ))}
          <input
            type="text"
            value={tagFilter}
            onChange={(e) => setTagFilter(e.target.value)}
            placeholder="Filtrar por tag..."
            className="px-3 py-1.5 text-sm bg-surface border border-outline-variant rounded-full w-40"
          />
          {tagFilter && (
            <button onClick={() => setTagFilter('')} className="text-xs text-primary hover:underline">Limpar tag</button>
          )}
        </div>

        {/* Lista de anotações */}
        <div className="space-y-3">
          {notes === null ? (
            notesError ? (
              <ErrorRetryCard message={notesError} onRetry={() => fetchNotes(filter, tagFilter, 0)} />
            ) : (
              <p className="text-sm text-on-surface-variant">Carregando...</p>
            )
          ) : notes.length === 0 ? (
            <p className="text-sm text-on-surface-variant">Seu caderno está vazio. Comece favoritando uma resposta no chat ou escreva uma reflexão acima.</p>
          ) : (
            notes.map((note) => (
              <div key={note.id} className="bg-surface-container-low border border-outline-variant rounded-lg p-4">
                {editingId === note.id ? (
                  <div className="space-y-2">
                    <input
                      type="text"
                      value={editDraft.title}
                      onChange={(e) => setEditDraft((prev) => ({ ...prev, title: e.target.value }))}
                      placeholder="Título (opcional)"
                      className="w-full p-2 bg-surface border border-outline-variant rounded"
                    />
                    <textarea
                      value={editDraft.content}
                      onChange={(e) => setEditDraft((prev) => ({ ...prev, content: e.target.value }))}
                      rows={4}
                      maxLength={8000}
                      className="w-full p-2 bg-surface border border-outline-variant rounded resize-none"
                    />
                    <input
                      type="text"
                      value={editDraft.tags}
                      onChange={(e) => setEditDraft((prev) => ({ ...prev, tags: e.target.value }))}
                      placeholder="Tags (separadas por vírgula)"
                      className="w-full p-2 bg-surface border border-outline-variant rounded text-sm"
                    />
                    <div className="flex justify-end gap-2">
                      <button onClick={cancelEdit} disabled={savingEdit} className="px-4 py-1.5 text-sm rounded border border-outline-variant hover:bg-surface-container-high disabled:opacity-50">
                        Cancelar
                      </button>
                      <button
                        onClick={() => handleSaveEdit(note.id)}
                        disabled={savingEdit || !editDraft.content.trim()}
                        className="px-4 py-1.5 text-sm bg-primary text-on-primary font-bold rounded hover:opacity-90 disabled:opacity-50"
                      >
                        {savingEdit ? 'Salvando...' : 'Salvar'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="flex justify-between items-start gap-3">
                      <div className="flex items-center gap-2 text-xs text-on-surface-variant">
                        <span className="material-symbols-outlined text-[16px]">{NOTE_TYPE_ICONS[note.note_type] || 'edit_note'}</span>
                        <span className="font-semibold">{note.title || NOTE_TYPE_LABELS[note.note_type] || note.note_type}</span>
                        <span>· {new Date(note.created_at).toLocaleDateString('pt-BR')}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <button onClick={() => startEdit(note)} title="Editar" className="text-on-surface-variant/50 hover:text-primary transition-colors">
                          <span className="material-symbols-outlined text-[18px]">edit</span>
                        </button>
                        <button onClick={() => handleDelete(note.id)} title="Apagar" className="text-on-surface-variant/50 hover:text-error transition-colors">
                          <span className="material-symbols-outlined text-[18px]">delete</span>
                        </button>
                      </div>
                    </div>
                    <p className="text-sm whitespace-pre-wrap mt-2">{note.content}</p>
                    {note.tags && note.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {note.tags.map((tag) => (
                          <button
                            key={tag}
                            onClick={() => setTagFilter(tag)}
                            className="text-[11px] px-2 py-0.5 rounded-full bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
                          >
                            #{tag}
                          </button>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            ))
          )}
          {notes && notes.length > 0 && notes.length < total && (
            <div className="flex justify-center pt-2">
              <button
                onClick={handleLoadMore}
                className="px-4 py-2 text-sm bg-surface-container-low border border-outline-variant rounded-lg hover:bg-surface-container-high transition-colors"
              >
                Carregar mais ({notes.length} de {total})
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
